/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Alert from "react-s-alert";

// import services
import PsychometricService from "../../services/PsychometricService";
import GeneralService from "../../services/GeneralService";

// local imports
import ErrorBoundary from "../general/ErrorBoundary";

const PsychometricOrganizations = () => {
  // states
  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [modal, setModal] = useState({ isVisible: false, selectedIndex: -1 });  
  const [editModal, setEditModal] = useState({
    isVisible: false,
    selectedIndex: -1,
    formData: {},
  });
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    totalPages: 1,
    totalResults: 0,
  });
  const [searchVal, setSearchVal] = useState("");
  let timeout = null;

  // useEffects
  useEffect(() => {
    if (searchVal) {
      fetchFilteredOrganizations();
    } else {
      fetchOrganizations();
    }
  }, [pagination.page, pagination.limit, searchVal]);

  const fetchFilteredOrganizations = async (page = pagination.page, limit = pagination.limit, search = searchVal) => {
    setIsLoading(true);
    try {
      const data = await PsychometricService.getFilteredOrganizations(limit, page, search);
      setOrganizations(data.users);
  
      if (data.pagination && typeof data.pagination.totalPages === "number" && typeof data.pagination.totalResults === "number") {
        setPagination((prev) => ({
          ...prev,
          totalPages: data.pagination.totalPages,
          totalResults: data.pagination.totalResults,
          page: data.pagination.page,
        }));
      } else {
        setPagination((prev) => ({
          ...prev,
          totalPages: 1,
          totalResults: data.users.length,
          page: 1,
        }));
      }
      setIsLoading(false);
    } catch (err) {
      Alert.error(
        "Some error occurred, please try again later or report to the support team.",
        {
          position: "top-right",
          effect: "slide",
        }
      );
      GeneralService.logError(
        "at PsychometricUsers fetchFilteredOrganizations",
        "Error in try catch block",
        err.stack
      );
      setIsLoading(false);
    }
  };
  const fetchOrganizations = async () => {
    setIsLoading(true);
    try {
      const data = await PsychometricService.getOrganizations(
        pagination.limit,
        pagination.page,
        searchVal
      );
      setOrganizations(data.organizations);
      console.log("data:", data);
      if (
        data.pagination &&
        typeof data.pagination.totalPages === "number" &&
        typeof data.pagination.totalResults === "number"
      ) {
        setPagination((prev) => ({
          ...prev,
          totalPages: data.pagination.totalPages,
          totalResults: data.pagination.totalResults,
          page: data.pagination.page,
        }));
      } else {
        setPagination((prev) => ({
          ...prev,
          totalPages: 1,
          totalResults: data.organizations.length,
          page: 1,
        }));
      }

      setIsLoading(false);
    } catch (err) {
      Alert.error(
        "Some error occurred, please try again later or report to the support team.",
        {
          position: "top-right",
          effect: "slide",
        }
      );
      GeneralService.logError(
        "at PsychometricOrganizations fetchOrganizations",
        "Error in try catch block",
        err.stack
      );
      setIsLoading(false);
    }
  };

  // Function to handle search input change
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchVal(value);
    setPagination({ ...pagination, page: 1 });
    fetchFilteredOrganizations(1, pagination.limit, value); 
  };

  // Function to handle pagination click
  const handlePagination = (e, index) => {
    e.preventDefault();
    setPagination({ ...pagination, page: index });
  };

  const toggleEditModal = (index) => {
    setEditModal({
      isVisible: true,
      selectedIndex: index,
      formData: {
        name: organizations[index].name,
        email: organizations[index].email,
        mobile: organizations[index].mobile,
        address: organizations[index].address,
        city: organizations[index].city,
        state: organizations[index].state,
        country: organizations[index].country,
        website: organizations[index].website,
      },
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditModal({
      ...editModal,
      formData: {
        ...editModal.formData,
        [name]: value,
      },
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const orgId = organizations[editModal.selectedIndex]._id;
    try {
     const result = await PsychometricService.updateOrganization(
        orgId,
        editModal.formData
      ); 
      if (result.success) {
        // Update organizations array in state with updated organization details
        const updatedOrganizations = [...organizations];
        updatedOrganizations[editModal.selectedIndex] = result.organization;
        setOrganizations(updatedOrganizations);
        setEditModal({ ...editModal, isVisible: false });
        Alert.success("Organization details updated successfully");
      } else {
        Alert.error("Failed to update organization details");
      }
    } catch (error) {
      console.error("Error updating organization:", error);
      Alert.error("Failed to update organization details");
    }
  };

  const handleDeleteClick = async (index) => {
    const orgId = organizations[index]._id;
    console.log("Deleting organization:", orgId);
    try {
      const result = await PsychometricService.deleteOrganization(orgId);
      if (result.success) {
        // Update organizations array in state by filtering out the deleted organization
        setOrganizations(organizations.filter((org, idx) => idx !== index));
        Alert.success("Organization deleted successfully");
      } else {
        Alert.error("Failed to delete organization");
      }
    } catch (error) {
      console.error("Error deleting organization:", error);
      Alert.error("Failed to delete organization");
    }
  };

  return (
    <main>
      <div className="container-fluid">
        <div className="d-flex my-4">
          <h3 className="my-auto">Psychometric Organizations</h3>
        </div>
        <div className="card mb-4">
          <div className="card-header">Organization List</div>
          <div className="card-body py-0">
            <div className="table-responsive">
              <Table
                bordered
                mb="0"
                id="dataTable"
                width="100%"
                cellSpacing="0"
              >
                <thead>
                  <tr>
                    <td colSpan="8" className="border-0 px-0">
                      <div className="d-flex">
                        <div className="ml-auto">
                          {/* Search bar */}
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search"
                            value={searchVal}
                            onChange={handleSearch}
                            style={{ width: "300px" }}
                          />

                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Country</th>
                    <th>Website</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
  {organizations.length > 0 ? (
    organizations.map((org, index) => (
      <ErrorBoundary
        key={index}
        errorAt={`At PsychometricOrganizations Table${index}`}
      >
        <tr>
          <td>{org.name}</td>
          <td>{org.email}</td>
          <td>{org.mobile}</td>
          <td>{org.address}</td>
          <td>{org.city}</td>
          <td>{org.state}</td>
          <td>{org.country}</td>
          <td>
            <a
              href={org.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              {org.website}
            </a>
          </td>
          <td>{org.isStudent === true ? "Academic" : "Corporate"}</td>
          <td>
            <a
              style={{
                color: "#384E73",
                marginRight: "10px",
                cursor: "pointer",
              }}
              onClick={() => toggleEditModal(index)}
            >
              Edit
            </a>
            <a
              style={{ color: "#FF6347", cursor: "pointer" }}
              onClick={() => handleDeleteClick(index)}
            >
              Delete
            </a>
          </td>
        </tr>
      </ErrorBoundary>
    ))
  ) : (
    <tr>
      <td colSpan="10">No organizations found</td>
    </tr>
  )}
</tbody>
                <tfoot>
                  <tr>
                    <td colSpan="10" className="border-0 pb-0 px-0">
                      <div className="d-flex">
                        <ErrorBoundary errorAt="At PsychometricOrganizations Pagination">
                          <div className="p-2">
                            Showing{" "}
                            {pagination.totalResults === 0
                              ? 0
                              : 1 +
                                pagination.limit * (pagination.page - 1)}{" "}
                            to{" "}
                            {pagination.limit * pagination.page >
                            pagination.totalResults
                              ? pagination.totalResults
                              : pagination.limit * pagination.page}{" "}
                            of {pagination.totalResults} entries
                          </div>
                          <Pagination
                            aria-label="Page navigation example"
                            className="ml-auto"
                          >
                            <PaginationItem disabled={pagination.page === 1}>
                              <PaginationLink
                                onClick={(e) =>
                                  handlePagination(e, pagination.page - 1)
                                }
                                previous
                                href="#"
                              />
                            </PaginationItem>
                            {[...Array(pagination.totalPages)].map((_, i) => (
                              <PaginationItem
                                active={i + 1 === pagination.page}
                                key={i}
                              >
                                <PaginationLink
                                  onClick={(e) => handlePagination(e, i + 1)}
                                  href="#"
                                >
                                  {i + 1}
                                </PaginationLink>
                              </PaginationItem>
                            ))}
                            <PaginationItem
                              disabled={
                                pagination.page === pagination.totalPages
                              }
                            >
                              <PaginationLink
                                onClick={(e) =>
                                  handlePagination(e, pagination.page + 1)
                                }
                                next
                                href="#"
                              />
                            </PaginationItem>
                          </Pagination>
                        </ErrorBoundary>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <Modal
        isOpen={modal.isVisible}
        toggle={() => setModal({ ...modal, isVisible: false })}
      >
        <ModalHeader toggle={() => setModal({ ...modal, isVisible: false })}>
          Organization Details
        </ModalHeader>
        <ModalBody>
          {modal.selectedIndex !== -1 && (
            <div>
              <p>Name: {organizations[modal.selectedIndex].name}</p>
              <p>Email: {organizations[modal.selectedIndex].email}</p>
              <p>Mobile: {organizations[modal.selectedIndex].mobile}</p>
              <p>Address: {organizations[modal.selectedIndex].address}</p>
              <p>City: {organizations[modal.selectedIndex].city}</p>
              <p>State: {organizations[modal.selectedIndex].state}</p>
              <p>Country: {organizations[modal.selectedIndex].country}</p>
              <p>
                Website:{" "}
                <a
                  href={organizations[modal.selectedIndex].website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {organizations[modal.selectedIndex].website}
                </a>
              </p>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal.isVisible}
        toggle={() => setEditModal({ ...editModal, isVisible: false })}
      >
        <ModalHeader
          toggle={() => setEditModal({ ...editModal, isVisible: false })}
        >
          Edit Organization
        </ModalHeader>
        <ModalBody>
          {editModal.selectedIndex !== -1 && (
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={editModal.formData.name}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={editModal.formData.email}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Mobile</label>
                <input
                  type="text"
                  name="mobile"
                  value={editModal.formData.mobile}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={editModal.formData.address}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={editModal.formData.city}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="state"
                  value={editModal.formData.state}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  value={editModal.formData.country}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="text"
                  name="website"
                  value={editModal.formData.website}
                  onChange={handleEditChange}
                  className="form-control"
                  required
                />
              </div>
              <Button type="submit" color="primary">
                Save
              </Button>
            </form>
          )}
        </ModalBody>
      </Modal>
    </main>
  );
};

export default PsychometricOrganizations;
